import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TaskEditorBlock,
  TaskEditorState,
  UNCATEGORIZED_SET_NUMBER,
} from "pages/task-editor/utils/taskEditorUtils";

// header 블록은 단독으로 reveal 되지 않는다. 다음 body 블록까지 같이 보여야
// 의미가 통하므로, advance 후 header 인 블록은 건너뛰며 다음 body 위치로 이동.
function expandPastHeaders(
  upTo: number,
  blocks: TaskEditorBlock[],
  hiddenSequences: ReadonlySet<number>
): number {
  let next = upTo;
  while (next < blocks.length) {
    const block = blocks[next - 1];
    if (!block) break;
    const isHeader = block.type === "header";
    const isHidden = hiddenSequences.has(next);
    if (!isHeader && !isHidden) break;
    next += 1;
  }
  return Math.min(next, blocks.length);
}

// ─── 타입 ─────────────────────────────────────────────────────────────────

export type PreviewSessionState = {
  revealedUpTo: number; // 보이는 마지막 블록 sequence (1-based). 정적 모드면 task.blocks.length
  hiddenSequences: ReadonlySet<number>; // 분기로 숨겨진 블록 sequence
  textInputs: Record<number, Record<number, string>>; // [blockIdx][fieldIdx] = value
  buttonSelections: Record<number, number[]>; // [blockIdx] = 선택된 옵션 indexes
  quizState: Record<number, { tried: number[]; correct: boolean }>;
};

export type PreviewSessionApi = {
  state: PreviewSessionState;
  reset: () => void;
  setTextInput: (blockIdx: number, fieldIdx: number, value: string) => void;
  toggleButtonDefault: (blockIdx: number, optionIdx: number) => void;
  attemptQuiz: (blockIdx: number, optionIdx: number) => void;
  chooseBranch: (blockIdx: number, optionIdx: number) => void;
  advanceFrom: (blockIdx: number) => void;
  isBlockComplete: (blockIdx: number) => boolean;
};

// ─── 초기 상태 생성 ───────────────────────────────────────────────────────

function makeInitialState(
  task: TaskEditorState,
  viewerMode: "patient" | "coach",
  isTrialEnabled: boolean
): PreviewSessionState {
  const hiddenSequences = new Set<number>();
  // 체험 ON + patient 탭에서만 첫 블록부터 reveal. 첫 블록이 header 라면
  // 다음 body 까지 같이 reveal.
  const initialUpTo =
    isTrialEnabled && viewerMode === "patient" ? 1 : task.blocks.length;
  const revealedUpTo =
    isTrialEnabled && viewerMode === "patient"
      ? expandPastHeaders(initialUpTo, task.blocks, hiddenSequences)
      : initialUpTo;
  return {
    revealedUpTo,
    hiddenSequences,
    textInputs: {},
    buttonSelections: {},
    quizState: {},
  };
}

// ─── hook ────────────────────────────────────────────────────────────────

function usePreviewSession(
  task: TaskEditorState | undefined,
  opts: { viewerMode: "patient" | "coach"; isTrialEnabled: boolean }
): PreviewSessionApi {
  const { viewerMode, isTrialEnabled } = opts;

  // task 가 undefined 일 때도 hook 을 안전하게 호출하기 위해 fallback 사용
  const safeTask: TaskEditorState = useMemo(
    () =>
      task ?? {
        task_id: "",
        title: "",
        set_number: UNCATEGORIZED_SET_NUMBER,
        blocks: [],
      },
    [task]
  );

  const [sessionState, setSessionState] = useState<PreviewSessionState>(() =>
    makeInitialState(safeTask, viewerMode, isTrialEnabled)
  );

  // reset 트리거 조건: task_id, blocks.length, viewerMode, isTrialEnabled
  const prevTaskIdRef = useRef(safeTask.task_id);
  const prevBlocksLengthRef = useRef(safeTask.blocks.length);
  const prevViewerModeRef = useRef(viewerMode);
  const prevIsTrialEnabledRef = useRef(isTrialEnabled);

  useEffect(() => {
    const taskIdChanged = prevTaskIdRef.current !== safeTask.task_id;
    const blocksLengthChanged =
      prevBlocksLengthRef.current !== safeTask.blocks.length;
    const viewerModeChanged = prevViewerModeRef.current !== viewerMode;
    const isTrialEnabledChanged =
      prevIsTrialEnabledRef.current !== isTrialEnabled;

    prevTaskIdRef.current = safeTask.task_id;
    prevBlocksLengthRef.current = safeTask.blocks.length;
    prevViewerModeRef.current = viewerMode;
    prevIsTrialEnabledRef.current = isTrialEnabled;

    if (
      taskIdChanged ||
      blocksLengthChanged ||
      viewerModeChanged ||
      isTrialEnabledChanged
    ) {
      setSessionState(makeInitialState(safeTask, viewerMode, isTrialEnabled));
    }
  }, [safeTask, viewerMode, isTrialEnabled]);

  const reset = useCallback(() => {
    setSessionState(makeInitialState(safeTask, viewerMode, isTrialEnabled));
  }, [safeTask, viewerMode, isTrialEnabled]);

  const setTextInput = useCallback(
    (blockIdx: number, fieldIdx: number, value: string) => {
      setSessionState((prev) => ({
        ...prev,
        textInputs: {
          ...prev.textInputs,
          [blockIdx]: {
            ...(prev.textInputs[blockIdx] ?? {}),
            [fieldIdx]: value,
          },
        },
      }));
    },
    []
  );

  const toggleButtonDefault = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_default") {
          return prev;
        }
        const allowMultiple = block.action.allow_multiple_selection;
        const current = prev.buttonSelections[blockIdx] ?? [];
        let next: number[];
        if (allowMultiple) {
          next = current.includes(optionIdx)
            ? current.filter((i) => i !== optionIdx)
            : [...current, optionIdx];
        } else {
          next = current.includes(optionIdx) ? [] : [optionIdx];
        }
        return {
          ...prev,
          buttonSelections: { ...prev.buttonSelections, [blockIdx]: next },
        };
      });
    },
    [safeTask.blocks]
  );

  const attemptQuiz = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_quiz") {
          return prev;
        }
        const option = block.action.options[optionIdx];
        const isCorrect = option?.is_correct === true;
        const prevQuiz = prev.quizState[blockIdx] ?? {
          tried: [],
          correct: false,
        };

        // v1 ButtonGroupCell 처럼 같은 옵션 다시 누르면 no-op. 다른 옵션을
        // 누르면 그 옵션을 last 로 옮긴다. correct 는 매 클릭마다 그 클릭의
        // 정/오답으로 갱신 — 정답 후 다른 오답 눌러도 alert 다시 표시.
        if (prevQuiz.tried[prevQuiz.tried.length - 1] === optionIdx) {
          return prev;
        }
        const newTried = [
          ...prevQuiz.tried.filter((i) => i !== optionIdx),
          optionIdx,
        ];
        const newQuizState = {
          ...prev.quizState,
          [blockIdx]: { tried: newTried, correct: isCorrect },
        };

        // advance 는 정답을 *처음* 맞춘 순간에만 1회. revealedUpTo 가 이미
        // 진행됐으면(이전에 정답 한 번 맞춘 후) 조건 안 맞아 재진행 X.
        const blockSeq = blockIdx + 1; // 1-based
        let newRevealedUpTo = prev.revealedUpTo;
        if (isCorrect && blockSeq === prev.revealedUpTo) {
          newRevealedUpTo = expandPastHeaders(
            prev.revealedUpTo + 1,
            safeTask.blocks,
            prev.hiddenSequences
          );
        }

        return {
          ...prev,
          revealedUpTo: newRevealedUpTo,
          quizState: newQuizState,
        };
      });
    },
    [safeTask.blocks]
  );

  const chooseBranch = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_branch") {
          return prev;
        }
        const options = block.action.options;
        const chosen = options[optionIdx];
        if (!chosen) return prev;

        const blockSeq = blockIdx + 1; // 1-based
        const showFrom = chosen.show_from ?? blockSeq + 1;

        // 다른 옵션들의 hide_from 을 hiddenSequences 에 추가
        const newHiddenSequences = new Set(prev.hiddenSequences);
        options.forEach((opt, idx) => {
          if (idx !== optionIdx && opt.hide_from !== undefined) {
            newHiddenSequences.add(opt.hide_from);
          }
        });

        const showFromExpanded = expandPastHeaders(
          showFrom,
          safeTask.blocks,
          newHiddenSequences
        );
        const newRevealedUpTo = Math.max(prev.revealedUpTo, showFromExpanded);

        return {
          ...prev,
          revealedUpTo: Math.min(newRevealedUpTo, safeTask.blocks.length),
          hiddenSequences: newHiddenSequences,
          buttonSelections: {
            ...prev.buttonSelections,
            [blockIdx]: [optionIdx],
          },
        };
      });
    },
    [safeTask.blocks]
  );

  const advanceFrom = useCallback(
    (blockIdx: number) => {
      setSessionState((prev) => {
        const blockSeq = blockIdx + 1; // 1-based
        if (blockSeq !== prev.revealedUpTo) return prev;

        const next = expandPastHeaders(
          prev.revealedUpTo + 1,
          safeTask.blocks,
          prev.hiddenSequences
        );
        return { ...prev, revealedUpTo: next };
      });
    },
    [safeTask.blocks]
  );

  const isBlockComplete = useCallback(
    (blockIdx: number): boolean => {
      const block = safeTask.blocks[blockIdx];
      if (!block) return false;
      const action = block.action;
      if (!action) return true;

      switch (action.type) {
        case "text_input": {
          // v1 logics.ts 와 동일 — optional 필드는 검증 제외, 필수 필드는
          // 비어있지 않아야 진행 (기본 필수).
          const values = sessionState.textInputs[blockIdx] ?? {};
          return action.fields.every(
            (field, fieldIdx) =>
              field.optional === true ||
              (values[fieldIdx] ?? "").trim().length > 0
          );
        }
        case "button_default": {
          const selections = sessionState.buttonSelections[blockIdx] ?? [];
          return selections.length >= 1;
        }
        case "button_quiz": {
          const quiz = sessionState.quizState[blockIdx];
          return quiz?.correct === true;
        }
        case "button_branch": {
          const selections = sessionState.buttonSelections[blockIdx] ?? [];
          return selections.length >= 1;
        }
        default: {
          // exhaustiveness check
          const _never: never = action;
          return _never;
        }
      }
    },
    [
      safeTask.blocks,
      sessionState.buttonSelections,
      sessionState.quizState,
      sessionState.textInputs,
    ]
  );

  return useMemo(
    () => ({
      state: sessionState,
      reset,
      setTextInput,
      toggleButtonDefault,
      attemptQuiz,
      chooseBranch,
      advanceFrom,
      isBlockComplete,
    }),
    [
      sessionState,
      reset,
      setTextInput,
      toggleButtonDefault,
      attemptQuiz,
      chooseBranch,
      advanceFrom,
      isBlockComplete,
    ]
  );
}

export default usePreviewSession;
